import { useLayoutEffect, useState } from 'react';

/**
 * The on-screen slice of the layout viewport, as seen by
 * `position: fixed` content.
 *
 * Opening the on-screen keyboard (iOS Safari, and Android Chrome since
 * 108) only shrinks the *visual* viewport. Fixed elements stay pinned to
 * the full-height layout viewport — partly behind the keyboard — and the
 * browser pans the page up to reveal the focused input, which can drag
 * the top of a fixed panel off screen.
 *
 *   - `bottomInset` — how far a fixed element's bottom edge must lift to
 *     sit on top of the keyboard (net of any pan the browser applied).
 *   - `height` — the visible height left above the keyboard.
 */
export interface VisibleViewport {
  bottomInset: number;
  height: number;
}

function readVisibleViewport(): VisibleViewport | null {
  const vv = window.visualViewport;
  // Pinch-zoom also shrinks the visual viewport; that isn't a keyboard,
  // so leave fixed content on its CSS defaults.
  if (!vv || vv.scale > 1.01) return null;
  return {
    bottomInset: Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)),
    height: Math.round(vv.height),
  };
}

/**
 * Subscribes to `visualViewport` resize / scroll only while `enabled`
 * (e.g. while a sheet is open). The last reading is kept once disabled
 * so a sheet that's animating closed doesn't jump. Returns `null` when
 * the API is unavailable or the page is pinch-zoomed.
 */
export function useVisibleViewport(enabled = true): VisibleViewport | null {
  const [viewport, setViewport] = useState<VisibleViewport | null>(null);

  useLayoutEffect(() => {
    const vv = window.visualViewport;
    if (!enabled || !vv) return;
    const update = () => {
      const next = readVisibleViewport();
      setViewport((prev) =>
        prev && next && prev.bottomInset === next.bottomInset && prev.height === next.height
          ? prev
          : next,
      );
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return viewport;
}
